import React, { useState, useRef, useEffect } from 'react';
import { Settings, LogOut, Edit, User, Play, Heart, Book, Camera, X, Sparkles, MessageCircle, Send, Bot, CheckCircle } from 'lucide-react';
import SettingsPage from './SettingsPage';
import LoadingScreen from './LoadingScreen';
import { useUserData } from '../hooks/useUserData';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';

const ProfilePage = () => {
  const [showSettings, setShowSettings] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [showFeedbackChat, setShowFeedbackChat] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [surveyResponses, setSurveyResponses] = useState({
    bedtimeProblems: [],
    cartoonzProblems: [],
    bedtimeAnswers: {},
    cartoonzAnswers: {},
    finalAnswers: {},
    otherBedtime: '',
    otherCartoonz: ''
  });
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { language } = useLanguage();
  const selectedLanguage = language === 'fr-FR' ? 'fr' : 'en';
  const [profileImage, setProfileImage] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [familyDescription, setFamilyDescription] = useState('');
  const fileInputRef = useRef(null);
  const { subscription, loading: userDataLoading, getStatusText } = useUserData();
  const { logout } = useAuth();

  // Get profile from localStorage immediately (same pattern as CartoonZPage/MyStoriesPage)
  const getProfileFromCache = () => {
    try {
      return {
        profileImage: localStorage.getItem('profileImage') || '',
        familyName: localStorage.getItem('familyName') || '',
        familyDescription: localStorage.getItem('familyDescription') || ''
      };
    } catch (error) {
      console.warn('Error loading profile from localStorage:', error);
      return { profileImage: '', familyName: '', familyDescription: '' };
    }
  };

  const cachedProfile = getProfileFromCache();
  const hasProfileData = cachedProfile.familyName || cachedProfile.profileImage || cachedProfile.familyDescription;

  // Translations object
  const translations = {
    en: {
      // Header
      "WhizZ Parent Insights": "WhizZ Parent Insights",
      "Step": "Step",
      "of": "of",
      
      // Testimonials
      "My child doesn't want to sleep": "My child doesn't want to sleep",
      "Store-bought stories are so repetitive": "Store-bought stories are so repetitive",
      "I'm out of creative story ideas": "I'm out of creative story ideas",
      "Screens excite instead of calming": "Screens excite instead of calming",
      "It takes 1 hour for bedtime": "It takes 1 hour for bedtime",
      "Sarah, mom of 2": "Sarah, mom of 2",
      "Mike, dad": "Mike, dad",
      "Emma, mom": "Emma, mom",
      "Lisa, mom": "Lisa, mom",
      "Tom, dad": "Tom, dad",
      "Sleep Resistance": "Sleep Resistance",
      "Content Quality": "Content Quality",
      "Creative Block": "Creative Block",
      "Screen Impact": "Screen Impact",
      "Time Management": "Time Management",
      
      // CartoonZ Testimonials
      "My child watches inappropriate YouTube content": "My child watches inappropriate YouTube content",
      "YouTube makes my child aggressive and hyper": "YouTube makes my child aggressive and hyper",
      "I can't control what ads they see": "I can't control what ads they see",
      "Most content is passive, not educational": "Most content is passive, not educational",
      "It's impossible to filter what they watch": "It's impossible to filter what they watch",
      "Anna, mom of 3": "Anna, mom of 3",
      "Chris, dad": "Chris, dad",
      "Maya, mom": "Maya, mom",
      "David, dad": "David, dad",
      "Sophie, mom": "Sophie, mom",
      "Content Control": "Content Control",
      "Behavioral Impact": "Behavioral Impact",
      "Ad Concerns": "Ad Concerns",
      "Educational Value": "Educational Value",
      "Safety Filtering": "Safety Filtering",
      
      // Step 1
      "What parents tell us about bedtime": "What parents tell us about bedtime",
      "Real challenges from real families": "Real challenges from real families",
      "Sound familiar?": "Sound familiar?",
      "Yes, I can relate": "Yes, I can relate",
      
      // Step 2
      "Which bedtime challenges do you face?": "Which bedtime challenges do you face?",
      "Select all that apply": "Select all that apply",
      "Bedtime resistance/fighting": "Bedtime resistance/fighting",
      "Repetitive, boring stories": "Repetitive, boring stories",
      "Running out of story ideas": "Running out of story ideas",
      "Screens are too stimulating": "Screens are too stimulating",
      "Long bedtime routines (1+ hours)": "Long bedtime routines (1+ hours)",
      "Other (please specify)": "Other (please specify)",
      "Back": "Back",
      "Continue": "Continue",
      "e.g. Night terrors, co-sleeping issues, early morning wake-ups, sibling disruption, travel disrupting routine...": "e.g. Night terrors, co-sleeping issues, early morning wake-ups, sibling disruption, travel disrupting routine...",
      
      // Step 3
      "We understand bedtime can be exhausting": "We understand bedtime can be exhausting",
      "Help us learn more about your routine": "Help us learn more about your routine",
      "How long does your child's bedtime routine usually take?": "How long does your child's bedtime routine usually take?",
      "15-30 minutes": "15-30 minutes",
      "30-60 minutes": "30-60 minutes",
      "1-2 hours": "1-2 hours",
      "More than 2 hours": "More than 2 hours",
      "What's your biggest bedtime challenge with your child?": "What's your biggest bedtime challenge with your child?",
      "Getting them to bed": "Getting them to bed",
      "Staying in bed": "Staying in bed",
      "Falling asleep": "Falling asleep",
      "Waking up at night": "Waking up at night",
      "Does your child fall asleep faster with WhizZ stories?": "Does your child fall asleep faster with WhizZ stories?",
      "Yes, much faster": "Yes, much faster",
      "Yes, a bit faster": "Yes, a bit faster",
      "About the same": "About the same",
      "I haven't tried WhizZ yet": "I haven't tried WhizZ yet",
      
      // Step 4
      "Now let's talk about screen time": "Now let's talk about screen time",
      "What parents say about YouTube and online content": "What parents say about YouTube and online content",
      "I relate to this too": "I relate to this too",
      
      // Step 5
      "Is your screen time problem the same?": "Is your screen time problem the same?",
      "Inappropriate YouTube content": "Inappropriate YouTube content",
      "Child becomes aggressive after YouTube": "Child becomes aggressive after YouTube",
      "Manipulative ads and algorithms": "Manipulative ads and algorithms",
      "Passive, non-educational content": "Passive, non-educational content",
      "Impossible to control what they see": "Impossible to control what they see",
      "e.g. Auto-play issues, addiction concerns, family conflicts over screen time, inappropriate recommendations...": "e.g. Auto-play issues, addiction concerns, family conflicts over screen time, inappropriate recommendations...",
      
      // Step 6
      "Screen time can be challenging too": "Screen time can be challenging too",
      "Help us understand your screen time concerns": "Help us understand your screen time concerns",
      "How much screen time does your child have daily?": "How much screen time does your child have daily?",
      "Less than 1 hour": "Less than 1 hour",
      "What concerns you most about YouTube/online content?": "What concerns you most about YouTube/online content?",
      "Inappropriate content": "Inappropriate content",
      "Excessive ads": "Excessive ads",
      "Addictive behavior": "Addictive behavior",
      "Lack of learning value": "Lack of learning value",
      "How does your child behave after watching YouTube vs CartoonZ?": "How does your child behave after watching YouTube vs CartoonZ?",
      "YouTube makes them hyper/aggressive": "YouTube makes them hyper/aggressive",
      "CartoonZ keeps them calmer": "CartoonZ keeps them calmer",
      "I haven't tried CartoonZ yet": "I haven't tried CartoonZ yet",
      
      // Step 7
      "Final questions to help us improve": "Final questions to help us improve",
      "Your insights will shape our solutions": "Your insights will shape our solutions",
      "Which is more important for your family?": "Which is more important for your family?",
      "Calm bedtime stories (WhizZ)": "Calm bedtime stories (WhizZ)",
      "Safe daytime content (CartoonZ)": "Safe daytime content (CartoonZ)",
      "Both equally important": "Both equally important",
      "Neither is a priority": "Neither is a priority",
      "How much would you pay monthly to eliminate YouTube from your child's routine?": "How much would you pay monthly to eliminate YouTube from your child's routine?",
      "$0 - wouldn't pay": "$0 - wouldn't pay",
      "$5-10": "$5-10",
      "$10-15": "$10-15",
      "$15+ if it really works": "$15+ if it really works",
      "What's your ideal screen time vs story time balance?": "What's your ideal screen time vs story time balance?",
      "Mostly stories, minimal screens": "Mostly stories, minimal screens",
      "50/50 balance": "50/50 balance",
      "Mostly screens with some stories": "Mostly screens with some stories",
      "All screens, no stories": "All screens, no stories",
      "Send Survey": "Send Survey",
      "Sending...": "Sending...",
      "2-4 hours": "2-4 hours",
      "More than 4 hours": "More than 4 hours"
    },
    fr: {
      // Header
      "WhizZ Parent Insights": "Enquête Parents WhizZ",
      "Step": "Étape",
      "of": "sur",
      
      // Testimonials
      "My child doesn't want to sleep": "Mon enfant ne veut pas dormir",
      "Store-bought stories are so repetitive": "Les histoires du commerce sont si répétitives",
      "I'm out of creative story ideas": "Je n'ai plus d'idées d'histoires créatives",
      "Screens excite instead of calming": "Les écrans excitent au lieu de calmer",
      "It takes 1 hour for bedtime": "Il faut 1 heure pour le coucher",
      "Sarah, mom of 2": "Sarah, maman de 2",
      "Mike, dad": "Mike, papa",
      "Emma, mom": "Emma, maman",
      "Lisa, mom": "Lisa, maman",
      "Tom, dad": "Tom, papa",
      "Sleep Resistance": "Résistance au sommeil",
      "Content Quality": "Qualité du contenu",
      "Creative Block": "Blocage créatif",
      "Screen Impact": "Impact des écrans",
      "Time Management": "Gestion du temps",
      
      // CartoonZ Testimonials
      "My child watches inappropriate YouTube content": "Mon enfant regarde du contenu YouTube inapproprié",
      "YouTube makes my child aggressive and hyper": "YouTube rend mon enfant agressif et hyperactif",
      "I can't control what ads they see": "Je ne peux pas contrôler les publicités qu'ils voient",
      "Most content is passive, not educational": "La plupart du contenu est passif, pas éducatif",
      "It's impossible to filter what they watch": "C'est impossible de filtrer ce qu'ils regardent",
      "Anna, mom of 3": "Anna, maman de 3",
      "Chris, dad": "Chris, papa",
      "Maya, mom": "Maya, maman",
      "David, dad": "David, papa",
      "Sophie, mom": "Sophie, maman",
      "Content Control": "Contrôle du contenu",
      "Behavioral Impact": "Impact comportemental",
      "Ad Concerns": "Préoccupations publicitaires",
      "Educational Value": "Valeur éducative",
      "Safety Filtering": "Filtrage de sécurité",
      
      // Step 1
      "What parents tell us about bedtime": "Ce que les parents nous disent du coucher",
      "Real challenges from real families": "Vrais défis de vraies familles",
      "Sound familiar?": "Ça vous rappelle quelque chose ?",
      "Yes, I can relate": "Oui, je me reconnais",
      
      // Step 2
      "Which bedtime challenges do you face?": "Quels défis du coucher rencontrez-vous ?",
      "Select all that apply": "Sélectionnez tout ce qui s'applique",
      "Bedtime resistance/fighting": "Résistance/bagarre au coucher",
      "Repetitive, boring stories": "Histoires répétitives et ennuyeuses",
      "Running out of story ideas": "Manque d'idées d'histoires",
      "Screens are too stimulating": "Les écrans sont trop stimulants",
      "Long bedtime routines (1+ hours)": "Routines longues du coucher (1h+)",
      "Other (please specify)": "Autre (veuillez préciser)",
      "Back": "Retour",
      "Continue": "Continuer",
      "e.g. Night terrors, co-sleeping issues, early morning wake-ups, sibling disruption, travel disrupting routine...": "ex. Terreurs nocturnes, problèmes de co-sleeping, réveils matinaux, perturbations des frères et sœurs, voyages perturbant la routine...",
      
      // Step 3
      "We understand bedtime can be exhausting": "Nous comprenons que le coucher peut être épuisant",
      "Help us learn more about your routine": "Aidez-nous à en savoir plus sur votre routine",
      "How long does your child's bedtime routine usually take?": "Combien de temps dure généralement la routine du coucher ?",
      "15-30 minutes": "15-30 minutes",
      "30-60 minutes": "30-60 minutes",
      "1-2 hours": "1-2 heures",
      "More than 2 hours": "Plus de 2 heures",
      "What's your biggest bedtime challenge with your child?": "Quel est votre plus grand défi au coucher ?",
      "Getting them to bed": "Les mettre au lit",
      "Staying in bed": "Rester au lit",
      "Falling asleep": "S'endormir",
      "Waking up at night": "Se réveiller la nuit",
      "Does your child fall asleep faster with WhizZ stories?": "Votre enfant s'endort-il plus vite avec les histoires WhizZ ?",
      "Yes, much faster": "Oui, beaucoup plus vite",
      "Yes, a bit faster": "Oui, un peu plus vite",
      "About the same": "À peu près pareil",
      "I haven't tried WhizZ yet": "Je n'ai pas encore essayé WhizZ",
      
      // Step 4
      "Now let's talk about screen time": "Maintenant parlons du temps d'écran",
      "What parents say about YouTube and online content": "Ce que disent les parents sur YouTube et le contenu en ligne",
      "I relate to this too": "Je me reconnais aussi",
      
      // Step 5
      "Is your screen time problem the same?": "Votre problème d'écran est-il le même ?",
      "Inappropriate YouTube content": "Contenu YouTube inapproprié",
      "Child becomes aggressive after YouTube": "L'enfant devient agressif après YouTube",
      "Manipulative ads and algorithms": "Publicités et algorithmes manipulateurs",
      "Passive, non-educational content": "Contenu passif, non éducatif",
      "Impossible to control what they see": "Impossible de contrôler ce qu'ils voient",
      "e.g. Auto-play issues, addiction concerns, family conflicts over screen time, inappropriate recommendations...": "ex. Problèmes de lecture automatique, préoccupations d'addiction, conflits familiaux sur le temps d'écran, recommandations inappropriées...",
      
      // Step 6
      "Screen time can be challenging too": "Le temps d'écran peut aussi être difficile",
      "Help us understand your screen time concerns": "Aidez-nous à comprendre vos préoccupations",
      "How much screen time does your child have daily?": "Combien de temps d'écran par jour ?",
      "Less than 1 hour": "Moins d'1 heure",
      "What concerns you most about YouTube/online content?": "Qu'est-ce qui vous préoccupe le plus ?",
      "Inappropriate content": "Contenu inapproprié",
      "Excessive ads": "Publicités excessives",
      "Addictive behavior": "Comportement addictif",
      "Lack of learning value": "Manque de valeur éducative",
      "How does your child behave after watching YouTube vs CartoonZ?": "Comment se comporte votre enfant après YouTube vs CartoonZ ?",
      "YouTube makes them hyper/aggressive": "YouTube les rend hyperactifs/agressifs",
      "CartoonZ keeps them calmer": "CartoonZ les garde plus calmes",
      "I haven't tried CartoonZ yet": "Je n'ai pas encore essayé CartoonZ",
      
      // Step 7
      "Final questions to help us improve": "Questions finales pour nous aider à améliorer",
      "Your insights will shape our solutions": "Vos idées façonneront nos solutions",
      "Which is more important for your family?": "Qu'est-ce qui est le plus important pour votre famille ?",
      "Calm bedtime stories (WhizZ)": "Histoires apaisantes (WhizZ)",
      "Safe daytime content (CartoonZ)": "Contenu sûr de jour (CartoonZ)",
      "Both equally important": "Les deux également importants",
      "Neither is a priority": "Ni l'un ni l'autre n'est prioritaire",
      "How much would you pay monthly to eliminate YouTube from your child's routine?": "Combien paieriez-vous pour éliminer YouTube ?",
      "$0 - wouldn't pay": "0€ - ne paierait pas",
      "$5-10": "5-10€",
      "$10-15": "10-15€",
      "$15+ if it really works": "15€+ si ça marche vraiment",
      "What's your ideal screen time vs story time balance?": "Quel est votre équilibre idéal écran vs histoires ?",
      "Mostly stories, minimal screens": "Surtout des histoires, peu d'écrans",
      "50/50 balance": "Équilibre 50/50",
      "Mostly screens with some stories": "Surtout des écrans avec quelques histoires",
      "All screens, no stories": "Que des écrans, pas d'histoires",
      "Send Survey": "Envoyer l'enquête",
      "Sending...": "Envoi en cours...",
      "2-4 hours": "2-4 heures",
      "More than 4 hours": "Plus de 4 heures",
      "Your Family Name": "Le nom de votre famille",
      "Edit Family Story": "Modifier l'histoire de famille",
      "Describe your family": "Décrivez votre famille",
      "Build with us": "Construisez avec nous",
      "Settings": "Paramètres",
      "Sign Out": "Se déconnecter",
      "WhizZ Stories": "Histoires WhizZ",
      "Profile Photo": "Photo de profil",
      "Upload a family photo to personalize your profile": "Ajoutez une photo de famille pour personnaliser votre profil",
      "Choose Photo": "Choisir une photo",
      "Cancel": "Annuler",
      "Save": "Enregistrer",
      "Family Name": "Nom de famille",
      "What's your family name?": "Quel est le nom de votre famille ?",
      "Enter your family name...": "Entrez le nom de votre famille...",
      "Family Story": "Histoire de famille",
      "Tell us about your family": "Parlez-nous de votre famille",
      "Describe your family's personality, interests, and what makes you special...": "Décrivez la personnalité de votre famille, ses centres d'intérêt, ce qui vous rend uniques...",
      "Inspiration ✨": "Inspiration ✨",
      "Hide": "Masquer",
      "Thank you! 🙏": "Merci ! 🙏",
      "Your insights have been sent to our team.": "Vos réponses ont été envoyées à notre équipe.",
      "They will help us build better solutions for families! 💚": "Elles nous aideront à créer de meilleures solutions pour les familles ! 💚",
      "Closing automatically in 7 seconds...": "Fermeture automatique dans 7 secondes..."
    }
  };

  // Translation function
  const t = (key) => {
    return translations[selectedLanguage][key] || key;
  };

  // Load profile data from backend with localStorage fallback
  useEffect(() => {
    if (!hasProfileData) {
      loadProfileData(); // Only call API if localStorage is empty (same as CartoonZ/MyStoriesPage)
    } else {
      // Use cache immediately (same as CartoonZ/MyStoriesPage pattern)
      setProfileImage(cachedProfile.profileImage);
      setFamilyName(cachedProfile.familyName);
      setFamilyDescription(cachedProfile.familyDescription);
    }
  }, []);

  const loadProfileData = async () => {
    try {
      // First try to get profile from backend
      try {
        const response = await apiService.user.getProfile();
        if (response.data.success && response.data.profile) {
          const profile = response.data.profile;
          
          // Set data from backend
          setProfileImage(profile.profileImage || '');
          setFamilyName(profile.familyName || '');
          setFamilyDescription(profile.familyDescription || '');
          
          console.log('✅ Profile data loaded from backend');
          return; // Successfully loaded from backend, exit early
        }
      } catch (backendError) {
        console.warn('⚠️ Backend profile not available, falling back to localStorage:', backendError.message);
      }
      
      // Fallback: Load from localStorage if backend fails
      setProfileImage(localStorage.getItem('profileImage') || '');
      setFamilyName(localStorage.getItem('familyName') || '');
      setFamilyDescription(localStorage.getItem('familyDescription') || '');
      console.log('📱 Profile data loaded from localStorage fallback');
      
    } catch (error) {
      console.error('❌ Error loading profile data:', error);
    }
  };

  const familySuggestions = [
    "Family of 5 – The Joyful Foodies\nThis lively crew lives for dancing in the kitchen, sharing delicious meals, and movie nights full of laughter. Life is a celebration, and they savor every moment together.",
    "Family of 3 soon to be 4 – The Dreamy Nesters\nA loving, close-knit family eagerly awaiting their newest addition. Their days are filled with cuddles, stories, gentle adventures, and dreams of what's to come.",
    "Family of 4 – The Adventure Seekers\nAlways ready for the next outdoor adventure, this family thrives on hiking trails, camping under stars, and exploring new places together.",
    "Family of 2 – The Creative Souls\nA dynamic duo who loves art, music, and storytelling. Their home is filled with creativity, laughter, and endless imagination.",
    "Family of 6 – The Sports Enthusiasts\nFrom soccer fields to basketball courts, this energetic family bonds over sports, cheering each other on and celebrating every victory together."
  ];

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageData = e.target.result;
        setProfileImage(imageData);
        
        // Save to localStorage for immediate feedback
        localStorage.setItem('profileImage', imageData);
        
        // Save to backend
        try {
          await apiService.user.updateProfile({ profileImage: imageData });
          console.log('✅ Profile image saved to backend');
        } catch (error) {
          console.warn('⚠️ Failed to save profile image to backend:', error);
        }
        
        setShowPhotoModal(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNameSave = async (name) => {
    setFamilyName(name);
    
    // Save to localStorage for immediate feedback  
    localStorage.setItem('familyName', name);
    
    // Save to backend
    try {
      await apiService.user.updateProfile({ familyName: name });
      console.log('✅ Family name saved to backend');
    } catch (error) {
      console.warn('⚠️ Failed to save family name to backend:', error);
    }
    
    setShowNameModal(false);
    
    // Dispatch custom event to notify other components (like WelcomePage)
    window.dispatchEvent(new CustomEvent('familyNameChanged'));
  };

  const handleDescriptionSave = async (description) => {
    setFamilyDescription(description);
    
    // Save to localStorage for immediate feedback
    localStorage.setItem('familyDescription', description);
    
    // Save to backend  
    try {
      await apiService.user.updateProfile({ familyDescription: description });
      console.log('✅ Family description saved to backend');
    } catch (error) {
      console.warn('⚠️ Failed to save family description to backend:', error);
    }
    
    setShowDescriptionModal(false);
  };
  
  const handleSignOut = async () => {
    try {
      await logout();
      // User will be automatically redirected to login by AuthWrapper
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) return;
    
    setIsTyping(true);
    
    try {
      // Simulate typing delay for better UX
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Save feedback to backend
      try {
        const feedbackData = {
          message: feedbackMessage,
          timestamp: new Date().toISOString(),
          user_id: 'anonymous_user', // Will be replaced with actual user ID when authenticated
          status: 'new'
        };
        
        await apiService.user.saveFeedback?.(feedbackData);
        console.log('✅ Feedback saved to backend');
      } catch (error) {
        console.warn('⚠️ Failed to save feedback to backend:', error);
        // Save to localStorage as fallback
        const existingFeedbacks = JSON.parse(localStorage.getItem('userFeedbacks') || '[]');
        existingFeedbacks.push({
          message: feedbackMessage,
          timestamp: new Date().toISOString()
        });
        localStorage.setItem('userFeedbacks', JSON.stringify(existingFeedbacks));
        console.log('📱 Feedback saved to localStorage fallback');
      }
      
      setFeedbackMessage('');
      setIsTyping(false);
      
    } catch (error) {
      console.error('❌ Error sending feedback:', error);
      setIsTyping(false);
    }
  };

  const handleSubmitSurvey = async () => {
    setIsTyping(true);
    
    try {
      // Compile full survey data
      const surveyData = {
        ...surveyResponses,
        timestamp: new Date().toISOString(),
        user_id: 'anonymous_user', // Will be replaced with actual user ID when authenticated
        type: 'parent_insights_survey',
        language: selectedLanguage // Include selected language
      };
      
      // Send email with survey results
      try {
        await apiService.user.sendSurveyEmail(surveyData);
        console.log('📧 Survey sent to email');
      } catch (error) {
        console.warn('⚠️ Failed to send email, saving locally:', error);
        // Fallback: save to localStorage
        const existingSurveys = JSON.parse(localStorage.getItem('parentSurveys') || '[]');
        existingSurveys.push(surveyData);
        localStorage.setItem('parentSurveys', JSON.stringify(existingSurveys));
        console.log('📱 Survey saved to localStorage fallback');
      }
      
      // Show modern success modal
      setShowSuccessModal(true);
      
      // Close survey and reset after 7 seconds
      setTimeout(() => {
        setShowFeedbackChat(false);
        resetSurvey();
        setShowSuccessModal(false);
      }, 7000);
      
      setIsTyping(false);
      
    } catch (error) {
      console.error('❌ Error submitting survey:', error);
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowFeedbackChat(false);
        resetSurvey();
        setShowSuccessModal(false);
      }, 7000);
      setIsTyping(false);
    }
  };

  const resetSurvey = () => {
    setCurrentStep(1);
    setSurveyResponses({
      bedtimeProblems: [],
      cartoonzProblems: [],
      bedtimeAnswers: {},
      cartoonzAnswers: {},
      finalAnswers: {},
      otherBedtime: '',
      otherCartoonz: ''
    });
  };
  
  // Helper function to scroll to top when changing steps
  const scrollToTop = () => {
    const surveyContent = document.querySelector('.survey-modal');
    if (surveyContent) {
      surveyContent.scrollTop = 0;
    }
  };
  
  // Navigation functions that include scroll to top
  const goToStep = (stepNumber) => {
    setCurrentStep(stepNumber);
    // Small delay to ensure the step has rendered before scrolling
    setTimeout(scrollToTop, 50);
  };
  
  if (showSettings) {
    return <SettingsPage onBack={() => setShowSettings(false)} />;
  }

  return (
    <div className="h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 overflow-y-auto">
      {/* Main Profile Content */}
      <div className="max-w-4xl mx-auto p-4 pb-32 md:pb-24">
        <div className="text-center mb-8">
          <div className="relative inline-block">
            <div 
              className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-300"
              onClick={() => setShowPhotoModal(true)}
            >
              {profileImage ? (
                <img 
                  src={profileImage} 
                  alt="Profile" 
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <User className="w-12 h-12 md:w-16 md:h-16 text-purple-500" />
              )}
            </div>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center shadow-md cursor-pointer hover:bg-purple-600 transition-colors hidden"
                 onClick={() => setShowPhotoModal(true)}>
              <Camera className="w-4 h-4 text-white" />
            </div>
          </div>
          
          {/* Family Name Section */}
          <div className="mb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                {familyName || t('Your Family Name')}
              </h1>
              <button 
                onClick={() => setShowNameModal(true)}
                className="p-1 hover:bg-purple-100 rounded-full transition-colors duration-200"
              >
                <Edit className="w-4 h-4 text-purple-500" />
              </button>
            </div>
            
            <p className="text-gray-600 text-sm md:text-base mb-3">
              {familyDescription || ''}
            </p>
            <button 
              onClick={() => setShowDescriptionModal(true)}
              className="text-purple-600 hover:text-purple-800 text-sm font-medium flex items-center gap-1 mx-auto transition-colors duration-200"
            >
              <Edit className="w-3 h-3" />
              {familyDescription ? t('Edit Family Story') : t('Describe your family')}
            </button>
          </div>
        </div>

        {/* Settings Button */}
        {showSettings && (
          <div className="fixed inset-0 z-50">
            <SettingsPage onClose={() => setShowSettings(false)} />
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-white/20 mx-auto max-w-md">
          {/* Content Cards */}
          <div className="p-4 space-y-3 pb-8">
            {/* WhizZ Status - Dynamic based on subscription */}
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-4 border-l-4 border-pink-400">
              <div className="flex items-center gap-2 mb-1">
                <Heart className="w-5 h-5 text-pink-500" />
                <h3 className="text-lg font-bold text-pink-600">{t('WhizZ Stories')}</h3>
              </div>
              <p className="text-pink-500 font-medium text-sm">
                {getStatusText}
              </p>
            </div>

            {/* Account Actions */}
            <div className="space-y-2 mt-4">
              {/* WhizZ Assistant Feedback */}
              <button 
                onClick={() => setShowFeedbackChat(true)}
                className="w-full bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 text-blue-700 py-3 px-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 border border-blue-200 hover:border-blue-300 group"
              >
                <MessageCircle className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
                {t('Build with us')}
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse ml-1"></div>
              </button>
              
              <button 
                onClick={() => setShowSettings(true)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Settings className="w-4 h-4" />
                {t('Settings')}
              </button>
              
              <button 
                onClick={handleSignOut}
                className="w-full bg-red-50 hover:bg-red-100 text-red-600 py-3 px-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                {t('Sign Out')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Upload Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm transform transition-all duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">{t('Profile Photo')}</h2>
                <button 
                  onClick={() => setShowPhotoModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-dashed border-purple-300">
                  {/* <Camera className="w-8 h-8 text-purple-500" /> */}
                </div>
                <p className="text-gray-600 text-sm mb-6">{t('Upload a family photo to personalize your profile')}</p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                
                <div className="space-y-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
                  >
                    Choose Photo
                  </button>
                  <button
                    onClick={() => setShowPhotoModal(false)}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Family Name Modal */}
      {showNameModal && (
        <FamilyNameModal 
          currentName={familyName}
          onSave={handleNameSave}
          onClose={() => setShowNameModal(false)}
          t={t}
        />
      )}

      {/* Family Description Modal */}
      {showDescriptionModal && (
        <FamilyDescriptionModal 
          currentDescription={familyDescription}
          suggestions={familySuggestions}
          onSave={handleDescriptionSave}
          onClose={() => setShowDescriptionModal(false)}
          t={t}
        />
      )}

      {/* WhizZ Assistant Feedback Chat - Multi-Step Survey */}
      {showFeedbackChat && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden transform transition-all duration-500 animate-in slide-in-from-bottom-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-6 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-cyan-400/20"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Bot className="w-6 h-6 text-white animate-pulse" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{t("WhizZ Parent Insights")}</h2>
                    <p className="text-blue-100 text-sm">{t("Step")} {currentStep} {t("of")} 7</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => {setShowFeedbackChat(false); resetSurvey();}}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors duration-200"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Survey Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh] survey-modal">
              {/* Step 1: Bedtime Testimonials */}
              {currentStep === 1 && (
                <BedtimeTestimonialsStep 
                  onNext={() => goToStep(2)}
                  t={t}
                />
              )}
              
              {/* Step 2: Bedtime Problems Selection */}
              {currentStep === 2 && (
                <BedtimeProblemsStep 
                  responses={surveyResponses}
                  setResponses={setSurveyResponses}
                  onNext={() => goToStep(3)}
                  onBack={() => goToStep(1)}
                  t={t}
                />
              )}
              
              {/* Step 3: WhizZ Questions */}
              {currentStep === 3 && (
                <WhizzQuestionsStep 
                  responses={surveyResponses}
                  setResponses={setSurveyResponses}
                  onNext={() => goToStep(4)}
                  onBack={() => goToStep(2)}
                  t={t}
                />
              )}
              
              {/* Step 4: CartoonZ Testimonials */}
              {currentStep === 4 && (
                <CartoonzTestimonialsStep 
                  onNext={() => goToStep(5)}
                  onBack={() => goToStep(3)}
                  t={t}
                />
              )}
              
              {/* Step 5: CartoonZ Problems Selection */}
              {currentStep === 5 && (
                <CartoonzProblemsStep 
                  responses={surveyResponses}
                  setResponses={setSurveyResponses}
                  onNext={() => goToStep(6)}
                  onBack={() => goToStep(4)}
                  t={t}
                />
              )}
              
              {/* Step 6: CartoonZ Questions */}
              {currentStep === 6 && (
                <CartoonzQuestionsStep 
                  responses={surveyResponses}
                  setResponses={setSurveyResponses}
                  onNext={() => goToStep(7)}
                  onBack={() => goToStep(5)}
                  t={t}
                />
              )}
              
              {/* Step 7: Final Questions */}
              {currentStep === 7 && (
                <FinalQuestionsStep 
                  responses={surveyResponses}
                  setResponses={setSurveyResponses}
                  onSubmit={handleSubmitSurvey}
                  onBack={() => goToStep(6)}
                  isSubmitting={isTyping}
                  t={t}
                />
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Modern Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md transform transition-all duration-500 animate-in zoom-in-95">
            <div className="p-8 text-center">
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => {
                    setShowFeedbackChat(false);
                    resetSurvey();
                    setShowSuccessModal(false);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                </button>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('Thank you! 🙏')}</h3>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                {t('Your insights have been sent to our team.')}<br/>
                {t('They will help us build better solutions for families! 💚')}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full animate-pulse"></div>
              </div>
              <p className="text-sm text-gray-400 mt-3">{t('Closing automatically in 7 seconds...')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Family Name Modal Component
const FamilyNameModal = ({ currentName, onSave, onClose, t }) => {
  const [name, setName] = React.useState(currentName);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              <h2 className="text-xl font-bold text-gray-800">{t('Family Name')}</h2>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("What's your family name?")}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('Enter your family name...')}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                autoFocus
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors duration-200"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={() => onSave(name)}
                className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
              >
                {t('Save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Family Description Modal Component
const FamilyDescriptionModal = ({ currentDescription, suggestions, onSave, onClose, t }) => {
  const [description, setDescription] = React.useState(currentDescription);
  const [showSuggestions, setShowSuggestions] = React.useState(true);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden transform transition-all duration-300">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500" />
              <h2 className="text-xl font-bold text-gray-800">{t('Family Story')}</h2>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Tell us about your family')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("Describe your family's personality, interests, and what makes you special...")}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 min-h-[120px] resize-none"
                autoFocus
              />
            </div>
            
            {showSuggestions && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">{t('Inspiration ✨')}</h3>
                  <button
                    onClick={() => setShowSuggestions(!showSuggestions)}
                    className="text-xs text-purple-600 hover:text-purple-800"
                  >
                    {t('Hide')}
                  </button>
                </div>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setDescription(suggestion)}
                      className="w-full text-left p-3 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-lg border border-purple-100 hover:border-purple-200 transition-all duration-200 group"
                    >
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line group-hover:text-gray-800">
                        {suggestion}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-100 bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-white hover:bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors duration-200 border border-gray-200"
            >
              {t('Cancel')}
            </button>
            <button
              onClick={() => onSave(description)}
              className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
            >
              {t('Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Survey Step Components

// Step 1: Bedtime Testimonials
const BedtimeTestimonialsStep = ({ onNext, t }) => {
  const testimonials = [
    { textKey: "My child doesn't want to sleep", authorKey: "Sarah, mom of 2", categoryKey: "Sleep Resistance" },
    { textKey: "Store-bought stories are so repetitive", authorKey: "Mike, dad", categoryKey: "Content Quality" },
    { textKey: "I'm out of creative story ideas", authorKey: "Emma, mom", categoryKey: "Creative Block" },
    { textKey: "Screens excite instead of calming", authorKey: "Lisa, mom", categoryKey: "Screen Impact" },
    { textKey: "It takes 1 hour for bedtime", authorKey: "Tom, dad", categoryKey: "Time Management" }
  ];

  return (
    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
      <div className="text-center space-y-2">
        <h3 className="text-2xl font-bold text-gray-900 leading-tight">{t("What parents tell us about bedtime")}</h3>
        <p className="text-gray-500 text-base">{t("Real challenges from real families")}</p>
      </div>
      
      <div className="space-y-4">
        {testimonials.map((testimonial, index) => (
          <div 
            key={index}
            className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 animate-in slide-in-from-left-2"
            style={{animationDelay: `${index * 0.1}s`}}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                    <div className="w-4 h-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full"></div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t(testimonial.authorKey)}</p>
                    <p className="text-xs text-gray-500">{t(testimonial.categoryKey)}</p>
                  </div>
                </div>
                <blockquote className="text-gray-700 text-base leading-relaxed font-medium">
                  "{t(testimonial.textKey)}"
                </blockquote>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="text-center pt-6">
        <div className="space-y-3">
          <p className="text-gray-600 text-sm">{t("Sound familiar?")}</p>
          <button
            onClick={onNext}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-8 rounded-2xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            {t("Yes, I can relate")}
          </button>
        </div>
      </div>
    </div>
  );
};

// Step 2: Bedtime Problems Selection
const BedtimeProblemsStep = ({ responses, setResponses, onNext, onBack, t }) => {
  const problems = [
    "Bedtime resistance/fighting",
    "Repetitive, boring stories",
    "Running out of story ideas", 
    "Screens are too stimulating",
    "Long bedtime routines (1+ hours)"
  ];

  const handleProblemToggle = (problem) => {
    const newProblems = responses.bedtimeProblems.includes(problem)
      ? responses.bedtimeProblems.filter(p => p !== problem)
      : [...responses.bedtimeProblems, problem];
    
    setResponses({...responses, bedtimeProblems: newProblems});
  };

  const handleOtherChange = (value) => {
    setResponses({...responses, otherBedtime: value});
  };

  return (
    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
      <div className="text-center space-y-2">
        <h3 className="text-2xl font-bold text-gray-900 leading-tight">{t("Which bedtime challenges do you face?")}</h3>
        <p className="text-gray-500 text-base">{t("Select all that apply")}</p>
      </div>
      
      <div className="space-y-3">
        {problems.map((problem, index) => (
          <label 
            key={index}
            className="flex items-center gap-4 p-5 bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 rounded-2xl transition-all duration-200 cursor-pointer group"
          >
            <div className="relative">
              <input
                type="checkbox"
                checked={responses.bedtimeProblems.includes(problem)}
                onChange={() => handleProblemToggle(problem)}
                className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              {responses.bedtimeProblems.includes(problem) && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            <span className="text-gray-800 group-hover:text-blue-800 font-medium text-base flex-1">{t(problem)}</span>
          </label>
        ))}
        
        {/* Other option */}
        <div className="p-5 bg-white border border-gray-200 rounded-2xl">
          <label className="flex items-center gap-4 mb-3">
            <div className="relative">
              <input
                type="checkbox"
                checked={responses.otherBedtime.length > 0}
                onChange={() => {}}
                className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              {responses.otherBedtime.length > 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            <span className="text-gray-800 font-medium text-base">{t("Other (please specify)")}</span>
          </label>
          <textarea
            value={responses.otherBedtime}
            onChange={(e) => handleOtherChange(e.target.value)}
            placeholder={t("e.g. Night terrors, co-sleeping issues, early morning wake-ups, sibling disruption, travel disrupting routine...")}
            className="w-full mt-2 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-base resize-none"
            rows={3}
          />
        </div>
      </div>
      
      <div className="flex gap-4 pt-6">
        <button
          onClick={onBack}
          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-4 px-6 rounded-2xl font-semibold transition-all duration-200"
        >
          {t("Back")}
        </button>
        <button
          onClick={onNext}
          disabled={responses.bedtimeProblems.length === 0 && responses.otherBedtime.trim() === ''}
          className="flex-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 text-white py-4 px-8 rounded-2xl font-semibold transition-all duration-200 disabled:cursor-not-allowed transform hover:scale-105 disabled:transform-none shadow-lg hover:shadow-xl"
        >
          {t("Continue")}
        </button>
      </div>
    </div>
  );
};

// Step 3: WhizZ Questions
const WhizzQuestionsStep = ({ responses, setResponses, onNext, onBack, t }) => {
  const questions = [
    {
      questionKey: "How long does your child's bedtime routine usually take?",
      optionKeys: ["15-30 minutes", "30-60 minutes", "1-2 hours", "More than 2 hours"],
      key: "bedtimeLength"
    },
    {
      questionKey: "What's your biggest bedtime challenge with your child?",
      optionKeys: ["Getting them to bed", "Staying in bed", "Falling asleep", "Waking up at night"],
      key: "biggestChallenge"
    },
    {
      questionKey: "Does your child fall asleep faster with WhizZ stories?",
      optionKeys: ["Yes, much faster", "Yes, a bit faster", "About the same", "I haven't tried WhizZ yet"],
      key: "whizzSpeed"
    }
  ];

  const handleAnswer = (questionKey, answer) => {
    setResponses({
      ...responses,
      bedtimeAnswers: {
        ...responses.bedtimeAnswers,
        [questionKey]: answer
      }
    });
  };

  const allAnswered = questions.every(q => responses.bedtimeAnswers[q.key]);

  return (
    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
      <div className="text-center space-y-2">
        <h3 className="text-2xl font-bold text-gray-900 leading-tight">{t("We understand bedtime can be exhausting")}</h3>
        <p className="text-gray-500 text-base">{t("Help us learn more about your routine")}</p>
      </div>
      
      {questions.map((q, index) => (
        <div key={index} className="space-y-3">
          <h4 className="font-medium text-gray-800">{t(q.questionKey)}</h4>
          <div className="space-y-2">
            {q.optionKeys.map((optionKey, optIndex) => (
              <label 
                key={optIndex}
                className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors duration-200 cursor-pointer group"
              >
                <input
                  type="radio"
                  name={q.key}
                  checked={responses.bedtimeAnswers[q.key] === optionKey}
                  onChange={() => handleAnswer(q.key, optionKey)}
                  className="w-4 h-4 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-gray-700 group-hover:text-blue-700 text-sm">{t(optionKey)}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      
      <div className="flex gap-3 pt-4">
        <button
          onClick={onBack}
          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors duration-200"
        >
          {t("Back")}
        </button>
        <button
          onClick={onNext}
          disabled={!allAnswered}
          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:from-gray-300 disabled:to-gray-400 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 disabled:cursor-not-allowed"
        >
          {t("Continue")}
        </button>
      </div>
    </div>
  );
};

// Step 4: CartoonZ Testimonials
const CartoonzTestimonialsStep = ({ onNext, onBack, t }) => {
  const testimonials = [
    { textKey: "My child watches inappropriate YouTube content", authorKey: "Anna, mom of 3", categoryKey: "Content Control" },
    { textKey: "YouTube makes my child aggressive and hyper", authorKey: "Chris, dad", categoryKey: "Behavioral Impact" },
    { textKey: "I can't control what ads they see", authorKey: "Maya, mom", categoryKey: "Ad Concerns" },
    { textKey: "Most content is passive, not educational", authorKey: "David, dad", categoryKey: "Educational Value" },
    { textKey: "It's impossible to filter what they watch", authorKey: "Sophie, mom", categoryKey: "Safety Filtering" }
  ];

  return (
    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
      <div className="text-center space-y-2">
        <h3 className="text-2xl font-bold text-gray-900 leading-tight">{t("Now let's talk about screen time")}</h3>
        <p className="text-gray-500 text-base">{t("What parents say about YouTube and online content")}</p>
      </div>
      
      <div className="space-y-4">
        {testimonials.map((testimonial, index) => (
          <div 
            key={index}
            className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-red-200 hover:shadow-lg transition-all duration-300 animate-in slide-in-from-left-2"
            style={{animationDelay: `${index * 0.1}s`}}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center">
                    <div className="w-4 h-4 bg-gradient-to-br from-red-500 to-orange-600 rounded-full"></div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t(testimonial.authorKey)}</p>
                    <p className="text-xs text-gray-500">{t(testimonial.categoryKey)}</p>
                  </div>
                </div>
                <blockquote className="text-gray-700 text-base leading-relaxed font-medium">
                  "{t(testimonial.textKey)}"
                </blockquote>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="flex gap-4 pt-6">
        <button
          onClick={onBack}
          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-4 px-6 rounded-2xl font-semibold transition-all duration-200"
        >
          {t("Back")}
        </button>
        <button
          onClick={onNext}
          className="flex-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-8 rounded-2xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          {t("I relate to this too")}
        </button>
      </div>
    </div>
  );
};

// Step 5: CartoonZ Problems Selection
const CartoonzProblemsStep = ({ responses, setResponses, onNext, onBack, t }) => {
  const problems = [
    "Inappropriate YouTube content",
    "Child becomes aggressive after YouTube",
    "Manipulative ads and algorithms",
    "Passive, non-educational content",
    "Impossible to control what they see"
  ];

  const handleProblemToggle = (problem) => {
    const newProblems = responses.cartoonzProblems.includes(problem)
      ? responses.cartoonzProblems.filter(p => p !== problem)
      : [...responses.cartoonzProblems, problem];
    
    setResponses({...responses, cartoonzProblems: newProblems});
  };

  const handleOtherChange = (value) => {
    setResponses({...responses, otherCartoonz: value});
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-800 mb-2">{t("Is your screen time problem the same?")}</h3>
        <p className="text-gray-600 text-sm">{t("Select all that apply")}</p>
      </div>
      
      <div className="space-y-3">
        {problems.map((problem, index) => (
          <label 
            key={index}
            className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-red-50 rounded-xl transition-colors duration-200 cursor-pointer group"
          >
            <input
              type="checkbox"
              checked={responses.cartoonzProblems.includes(problem)}
              onChange={() => handleProblemToggle(problem)}
              className="w-5 h-5 text-red-500 rounded focus:ring-red-500"
            />
            <span className="text-gray-700 group-hover:text-red-700 font-medium">{t(problem)}</span>
          </label>
        ))}
        
        {/* Other option */}
        <div className="p-4 bg-gray-50 rounded-xl">
          <label className="flex items-center gap-3 mb-2">
            <input
              type="checkbox"
              checked={responses.otherCartoonz.length > 0}
              onChange={() => {}}
              className="w-5 h-5 text-red-500 rounded focus:ring-red-500"
            />
            <span className="text-gray-700 font-medium">{t("Other (please specify)")}</span>
          </label>
          <textarea
            value={responses.otherCartoonz}
            onChange={(e) => handleOtherChange(e.target.value)}
            placeholder={t("e.g. Auto-play issues, addiction concerns, family conflicts over screen time, inappropriate recommendations...")}
            className="w-full mt-2 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
            rows={3}
          />
        </div>
      </div>
      
      <div className="flex gap-3 pt-4">
        <button
          onClick={onBack}
          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors duration-200"
        >
          {t("Back")}
        </button>
        <button
          onClick={onNext}
          disabled={responses.cartoonzProblems.length === 0 && responses.otherCartoonz.trim() === ''}
          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:from-gray-300 disabled:to-gray-400 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 disabled:cursor-not-allowed"
        >
          {t("Continue")}
        </button>
      </div>
    </div>
  );
};

// Step 6: CartoonZ Questions
const CartoonzQuestionsStep = ({ responses, setResponses, onNext, onBack, t }) => {
  const questions = [
    {
      questionKey: "How much screen time does your child have daily?",
      optionKeys: ["Less than 1 hour", "1-2 hours", "2-4 hours", "More than 4 hours"],
      key: "screenTime"
    },
    {
      questionKey: "What concerns you most about YouTube/online content?",
      optionKeys: ["Inappropriate content", "Excessive ads", "Addictive behavior", "Lack of learning value"],
      key: "mainConcern"
    },
    {
      questionKey: "How does your child behave after watching YouTube vs CartoonZ?",
      optionKeys: ["YouTube makes them hyper/aggressive", "CartoonZ keeps them calmer", "About the same", "I haven't tried CartoonZ yet"],
      key: "behaviorDifference"
    }
  ];

  const handleAnswer = (questionKey, answer) => {
    setResponses({
      ...responses,
      cartoonzAnswers: {
        ...responses.cartoonzAnswers,
        [questionKey]: answer
      }
    });
  };

  const allAnswered = questions.every(q => responses.cartoonzAnswers[q.key]);

  return (
    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
      <div className="text-center space-y-2">
        <h3 className="text-2xl font-bold text-gray-900 leading-tight">{t("Screen time can be challenging too")}</h3>
        <p className="text-gray-500 text-base">{t("Help us understand your screen time concerns")}</p>
      </div>
      
      {questions.map((q, index) => (
        <div key={index} className="space-y-3">
          <h4 className="font-medium text-gray-800">{t(q.questionKey)}</h4>
          <div className="space-y-2">
            {q.optionKeys.map((optionKey, optIndex) => (
              <label 
                key={optIndex}
                className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-red-50 rounded-lg transition-colors duration-200 cursor-pointer group"
              >
                <input
                  type="radio"
                  name={q.key}
                  checked={responses.cartoonzAnswers[q.key] === optionKey}
                  onChange={() => handleAnswer(q.key, optionKey)}
                  className="w-4 h-4 text-red-500 focus:ring-red-500"
                />
                <span className="text-gray-700 group-hover:text-red-700 text-sm">{t(optionKey)}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      
      <div className="flex gap-3 pt-4">
        <button
          onClick={onBack}
          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors duration-200"
        >
          {t("Back")}
        </button>
        <button
          onClick={onNext}
          disabled={!allAnswered}
          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:from-gray-300 disabled:to-gray-400 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 disabled:cursor-not-allowed"
        >
          {t("Continue")}
        </button>
      </div>
    </div>
  );
};

// Step 7: Final Questions
const FinalQuestionsStep = ({ responses, setResponses, onSubmit, onBack, isSubmitting, t }) => {
  const questions = [
    {
      questionKey: "Which is more important for your family?",
      optionKeys: ["Calm bedtime stories (WhizZ)", "Safe daytime content (CartoonZ)", "Both equally important", "Neither is a priority"],
      key: "priority"
    },
    {
      questionKey: "How much would you pay monthly to eliminate YouTube from your child's routine?",
      optionKeys: ["$0 - wouldn't pay", "$5-10", "$10-15", "$15+ if it really works"],
      key: "youtubeReplacement"
    },
    {
      questionKey: "What's your ideal screen time vs story time balance?",
      optionKeys: ["Mostly stories, minimal screens", "50/50 balance", "Mostly screens with some stories", "All screens, no stories"],
      key: "balance"
    }
  ];

  const handleAnswer = (questionKey, answer) => {
    setResponses({
      ...responses,
      finalAnswers: {
        ...responses.finalAnswers,
        [questionKey]: answer
      }
    });
  };

  const allAnswered = questions.every(q => responses.finalAnswers[q.key]);

  return (
    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
      <div className="text-center space-y-2">
        <h3 className="text-2xl font-bold text-gray-900 leading-tight">{t("Final questions to help us improve")}</h3>
        <p className="text-gray-500 text-base">{t("Your insights will shape our solutions")}</p>
      </div>
      
      {questions.map((q, index) => (
        <div key={index} className="space-y-3">
          <h4 className="font-medium text-gray-800">{t(q.questionKey)}</h4>
          <div className="space-y-2">
            {q.optionKeys.map((optionKey, optIndex) => (
              <label 
                key={optIndex}
                className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-green-50 rounded-lg transition-colors duration-200 cursor-pointer group"
              >
                <input
                  type="radio"
                  name={q.key}
                  checked={responses.finalAnswers[q.key] === optionKey}
                  onChange={() => handleAnswer(q.key, optionKey)}
                  className="w-4 h-4 text-green-500 focus:ring-green-500"
                />
                <span className="text-gray-700 group-hover:text-green-700 text-sm">{t(optionKey)}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      
      <div className="flex gap-3 pt-4">
        <button
          onClick={onBack}
          disabled={isSubmitting}
          className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors duration-200"
        >
          {t("Back")}
        </button>
        <button
          onClick={onSubmit}
          disabled={!allAnswered || isSubmitting}
          className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-gray-300 disabled:to-gray-400 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              {t("Sending...")}
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              {t("Send Survey")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProfilePage;ProfilePage;